import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { UserService } from '@core/user.service';
import { ApiResponse } from '@models/api-response';
import { ProductEventNotification } from '@models/product-event-notification';
import { instantiateApiResponseFromArray } from '@shared/zb-rxjs-operators/rxjs-operators';
import { Observable, map, of } from 'rxjs';
import { AppConfigService } from '../../../appconfig.service';

/**
 * Handles getting notifications for events made to Products by masqueraded admins.
 * Currently only ECE has product event notifications.
 * These only show to the user once after they've logged in and navigated to a product.
 * The first time a non-masqueraded Teacher lands on the ECE planner, ECE calls this service to get the notification.
 *
 * Contains State and API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductEventNotificationService implements StatefulService {
  notifications: ProductEventNotification[] = [];

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private userService: UserService,
  ) { }

  private getEventHistoryUrl(userId?: string): string {
    const baseUrl = `${this.appConfig.apiUrl}/user/event-history`;
    return userId ? `${baseUrl}/${userId}` : baseUrl;
  }

  getNotifications(): Observable<ApiResponse<ProductEventNotification[]>> {
    if (!this.userService.isMasqueraded && this.userService.userId) {
      const url: string = this.getEventHistoryUrl(this.userService.userId);
      return this.http.get(url)
        .pipe(
          map((apiRes: any) => {
            if (apiRes === null) {
              return { response: {}, messages: [] };
            }
            this.notifications = apiRes.response;
            return apiRes;
          }),
          instantiateApiResponseFromArray(ProductEventNotification),
        );
    }
    return of(null);
  }

  dismissNotification(eventHistoryId: string): Observable<ApiResponse<boolean>> {
    if (!this.userService.isMasqueraded) {
      const url: string = `${this.appConfig.apiUrl}/user/event-history/${eventHistoryId}`;
      return this.http.patch(url, {})
        .pipe(
          map(() => new ApiResponse<boolean>(true, { messages: [] })),
        );
    }
    return of(null);
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this.notifications = [];
  }
}
